import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import * as PostProcessing from '@react-three/postprocessing';
import { forwardRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface GridEffectProperties extends EffectProperties {
  scale: number;
  lineWidth: number;
  gridSize: {
    width: number;
    height: number;
  };
}

// Create a reusable Grid component that can be used in both preview and world
const GridComponent = forwardRef<any, { properties: GridEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  
  // IMPORTANT: Return null immediately in preview mode to prevent WebGL errors
  if (isPreview) {
    return null;
  }
  
  const { scale, lineWidth, gridSize, blendFunction } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.NORMAL;

  return (
    <PostProcessing.Grid
      scale={scale}
      lineWidth={lineWidth}
      size={gridSize}
      blendFunction={blendFunctionValue}
    />
  );
});

export const GridEffectItem: Effect<GridEffectProperties> = {
  id: '',
  name: 'Grid',
  type: 'effect',
  subtype: 'Grid',
  icon: 'Effect',
  properties: {
    scale: 1.0,
    lineWidth: 0.0,
    gridSize: {
      width: 1.0,
      height: 1.0
    },
    blendFunction: 'NORMAL',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.scale',
      type: 'number',
      label: 'Scale',
      default: 1.0,
      min: 0.1,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.lineWidth',
      type: 'number',
      label: 'Line Width',
      default: 0.0,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.gridSize.width',
      type: 'number',
      label: 'Grid Width',
      default: 1.0,
      min: 0.1,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.gridSize.height',
      type: 'number',
      label: 'Grid Height',
      default: 1.0,
      min: 0.1,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'NORMAL',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    }
  ],
  render: (properties: GridEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <GridComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 